import React, {useCallback, useEffect, useMemo, useState} from 'react';
import useMrcStore from 'common/stores/mrcStore';
import {shallow} from 'zustand/shallow';
import {useTranslation} from 'react-i18next';
import debounce from 'lodash/debounce';

import {Layout, Text, TextField, WarningIcon} from '@phonectas/unifon-ui-kit';
import {
	BrregApiResponse,
	BrregEnhet,
	BrregSlettetEnhet,
	MrcEntryCreateRequest,
} from 'common/types/store/mrcTypes';

interface Props {
	requiredFields: string[];
	canSendMrc: (canSend: boolean) => void;
	onFormDataChange: (data: Partial<MrcEntryCreateRequest>) => void;
}

// Map backend fields to UI labels and types
export const FIELD_CONFIG: Record<string, {label: string; type: 'text' | 'orgnr' | 'date'; group: 'from' | 'to' | 'other' | 'default'}> = {
	phone_number: {label: 'Mobile', type: 'text', group: 'default'},
	from_vat_no: {label: 'Org. number', type: 'orgnr', group: 'from'},
	to_vat_no: {label: 'Org. number', type: 'orgnr', group: 'to'},
	from_company_name: {label: 'Company name', type: 'text', group: 'from'},
	to_company_name: {label: 'Company name', type: 'text', group: 'to'},
	from_person_name: {label: 'Full name', type: 'text', group: 'from'},
	to_person_name: {label: 'Full name', type: 'text', group: 'to'},
	from_birth_date: {label: 'Birth date (dd.mm.yyyy)', type: 'date', group: 'from'},
	to_birth_date: {label: 'Birth date (dd.mm.yyyy)', type: 'date', group: 'to'},
	// Add more mappings as needed
};

// Type guards for BrregApiResponse
function isBrregEnhet(obj: BrregApiResponse): obj is BrregEnhet {
	return obj && (obj as BrregEnhet).respons_klasse === 'Enhet';
}
function isBrregSlettetEnhet(obj: BrregApiResponse): obj is BrregSlettetEnhet {
	return obj && (obj as BrregSlettetEnhet).respons_klasse === 'SlettetEnhet';
}
function isBrregErrorResponse(obj: BrregApiResponse): obj is {respons_klasse: 'ERROR'} {
	return obj && (obj as {respons_klasse: 'ERROR'}).respons_klasse === 'ERROR';
}

const MrcContent: React.FC<Props> = ({
	requiredFields = [],
	canSendMrc,
	onFormDataChange,
}) => {
	const [t] = useTranslation();
	const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
	const {fetchBrregOrg} = useMrcStore();
	const {orgData} = useMrcStore((state) => ({
		orgData: state.activeBrregOrg,
	}), shallow);
	// Track which orgnr field was last updated
	const [lastOrgField, setLastOrgField] = useState<string | null>(null);

	// Debounced fetch function
	const debouncedFetchBrregOrg = useMemo(
		() =>
			debounce(async (value: string, fieldKey: string) => {
				setLastOrgField(fieldKey);
				await fetchBrregOrg(value);
			}, 500),
		[fetchBrregOrg]
	);

	// Auto-fill company name when org data is fetched
	useEffect(() => {
		if (!lastOrgField) return;
		if (orgData && isBrregEnhet(orgData)) {
			if (lastOrgField === 'from_vat_no') {
				setFieldValues((prev) => ({
					...prev,
					from_company_name: orgData.navn,
				}));
			} else if (lastOrgField === 'to_vat_no') {
				setFieldValues((prev) => ({
					...prev,
					to_company_name: orgData.navn,
				}));
			}
		}
		 
	}, [orgData, lastOrgField]);

	useEffect(() => {
		if (!lastOrgField) return;
		if (orgData && isBrregErrorResponse(orgData)) {
			if (lastOrgField === 'from_vat_no') {
				setFieldValues((prev) => ({
					...prev,
					from_company_name: '',
				}));
			} else if (lastOrgField === 'to_vat_no') {
				setFieldValues((prev) => ({
					...prev,
					to_company_name: '',
				}));
			}
		}
	}, [orgData, lastOrgField]);

	const prepareFormData = useCallback(() => {
		const formData = Object.entries(fieldValues).reduce<Record<string, string>>((acc, [key, value]) => {
			if (value !== null && value !== undefined && value !== '') {
				acc[key] = value;
			}
			return acc;
		}, {});
		return formData as Partial<MrcEntryCreateRequest>;
	}, [fieldValues]);

	useEffect(() => {
		onFormDataChange(prepareFormData());
	}, [prepareFormData, onFormDataChange]);

	const formatBirthDate = (value: string): string => {
		// Remove all non-digit characters
		const digits = value.replace(/\D/g, '');
		
		// Don't format if empty
		if (digits.length === 0) return '';
		
		// Add dots at appropriate positions
		if (digits.length <= 2) {
			return digits;
		} else if (digits.length <= 4) {
			return `${digits.slice(0, 2)}.${digits.slice(2)}`;
		} else {
			return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4, 8)}`;
		}
	};

	const handleFieldChange = useCallback(
		(field: string, value: string) => {
			let processedValue = value;
			
			// Auto-format birth date fields
			if (field === 'from_birth_date' || field === 'to_birth_date') {
				processedValue = formatBirthDate(value);
			}
			
			setFieldValues((prev) => ({...prev, [field]: processedValue}));
			
			// If orgnr field, trigger debounced fetch
			if ((field === 'from_vat_no' || field === 'to_vat_no') && value.length === 9 && /^\d+$/.test(value)) {
				debouncedFetchBrregOrg(value, field);
			}
		},
		[debouncedFetchBrregOrg]
	);
	// const handleFieldChange = useCallback(
	// 	(field: string, value: string) => {
	// 		setFieldValues((prev) => ({...prev, [field]: value}));
	// 		// If orgnr field, trigger debounced fetch
	// 		if ((field === 'from_vat_no' || field === 'to_vat_no') && value.length === 9 && /^\d+$/.test(value)) {
	// 			debouncedFetchBrregOrg(value, field);
	// 		}
	// 	},
	// 	[debouncedFetchBrregOrg]
	// );

	const inputPropsMap: Record<string, React.InputHTMLAttributes<HTMLInputElement>> = {
		from_vat_no: {minLength: 9, maxLength: 9, pattern: '\\d{9}'},
		to_vat_no: {minLength: 9, maxLength: 9, pattern: '\\d{9}'},
		phone_number: {minLength: 8, maxLength: 11, pattern: '(\\+47\\d{8}|\\d{8})'},
		from_company_name: {minLength: 1},
		to_company_name: {minLength: 1},
		from_person_name: {minLength: 1},
		to_person_name: {minLength: 1},
		from_birth_date: {minLength: 10, maxLength: 10, pattern: '\\d{2}\\.\\d{2}\\.\\d{4}'},
		to_birth_date: {minLength: 10, maxLength: 10, pattern: '\\d{2}\\.\\d{2}\\.\\d{4}'},
	};
	// const inputPropsMap: Record<string, React.InputHTMLAttributes<HTMLInputElement>> = {
	// 	from_vat_no: {minLength: 9, maxLength: 9, pattern: '\\d{9}'},
	// 	to_vat_no: {minLength: 9, maxLength: 9, pattern: '\\d{9}'},
	// 	phone_number: {minLength: 8, maxLength: 11, pattern: '(\\+47\\d{8}|\\d{8})'},
	// 	from_company_name: {minLength: 1},
	// 	to_company_name: {minLength: 1},
	// 	from_person_name: {minLength: 1},
	// 	to_person_name: {minLength: 1},
	// 	from_birth_date: {pattern: '\\d{2}\\.\\d{2}\\.\\d{4}'},
	// 	to_birth_date: {pattern: '\\d{2}\\.\\d{2}\\.\\d{4}'},
	// };

	// Group fields for headings
	const fromFields = requiredFields.filter(f => (FIELD_CONFIG[f]?.group === 'from'));
	const toFields = requiredFields.filter(f => (FIELD_CONFIG[f]?.group === 'to'));
	const otherFields = requiredFields.filter(f => !FIELD_CONFIG[f] || FIELD_CONFIG[f].group === 'other');

	const renderFields = (fields: string[], _group: string) => {
		return (
			<Layout direction="row" gap="md" fullWidth nowrap>
				{fields.map((field) => {
					const config = FIELD_CONFIG[field] || {label: field, type: 'text', group: 'other'};

					if (
						(field === 'from_company_name' && !fields.includes('from_vat_no')) ||
						(field === 'to_company_name' && !fields.includes('to_vat_no'))
					) {
						return null;
					}

					return (
						<Layout
							key={field}
							style={{marginBottom: 12}}
							direction="column"
							fullWidth
						>
							<TextField
								label={t(config.label)}
								fullWidth
								value={fieldValues[field] || ''}
								onChange={(e) => handleFieldChange(field, e.target.value)}
								disabled={field === 'from_company_name' || field === 'to_company_name'}
								inputProps={inputPropsMap[field] || {}}
								errorMessagePlacement="none"
							/>
						</Layout>
					);
				})}
			</Layout>
		);
	};

	useEffect(() => {
		if (typeof canSendMrc !== 'function') return;

		const isValid = (field: string, value: string) => {
			const props = inputPropsMap[field];
			if (!props) return value.trim().length > 0;

			if (props.minLength && value.length < props.minLength) return false;
			if (props.pattern && !(new RegExp(props.pattern).test(value))) return false;
			if (props.maxLength && value.length > props.maxLength) return false;
			return value.trim().length > 0;
		};

		const allValid = requiredFields.every(field => {
			if (field === 'from_vat_no' && !requiredFields.includes('from_company_name')) {
				return isValid('from_company_name', fieldValues['from_company_name'] || '');
			}
			if (field === 'to_vat_no' && !requiredFields.includes('to_company_name')) {
				return isValid('to_company_name', fieldValues['to_company_name'] || '');
			}
			return isValid(field, fieldValues[field] || '');
		});

		canSendMrc(allValid);
	}, [fieldValues, requiredFields, canSendMrc]);

	return (
		<Layout fullWidth gap="xs" direction="column">
			<Layout fullWidth justifyContent="center">
				{(orgData && isBrregSlettetEnhet(orgData)) && (
					<Layout gap="xs" alignItems="center" style={{color: '#F7AB20', marginBottom: 8}}>
						<WarningIcon color="warning" />
						<Text color="warning">{orgData.navn} {t('is deleted in the Brønnøysund Register')}</Text>
					</Layout>
				)}
				{(orgData && isBrregErrorResponse(orgData)) && (
					<Layout gap="xs" alignItems="center" style={{color: '#EB5757', marginBottom: 8}}>
						<WarningIcon color="error" />
						<Text color="error">{t('Organization not found in the Brønnøysund Register')}</Text>
					</Layout>
				)}
			</Layout>
			{/* Always show Mobile at the top if present */}
			{requiredFields.includes('phone_number') && (
				<Layout style={{width: 276, marginBottom: 12}} direction="column">
					<TextField
						label={t('Mobile')}
						fullWidth
						value={fieldValues['phone_number'] || ''}
						onChange={e => handleFieldChange('phone_number', e.target.value)}
					/>
				</Layout>
			)}

			{/* Current legal owner */}
			{fromFields.length > 0 && (
				<>
					<Text variant="h2" style={{marginBottom: 8}}>{t('Current legal owner')}:</Text>
					{renderFields(
						fromFields.includes('from_company_name')
							? fromFields
							: [
								...fromFields,
								fromFields.includes('from_vat_no') && !fromFields.includes('from_company_name')
									? 'from_company_name'
									: null,
							].filter(Boolean) as string[],
						'from'
					)}
				</>
			)}

			{/* New legal owner */}
			{toFields.length > 0 && (
				<>
					<Text variant="h2" style={{marginBottom: 8}}>{t('New legal owner')}:</Text>
					{renderFields(
						toFields.includes('to_company_name')
							? toFields
							: [
								...toFields,
								toFields.includes('to_vat_no') && !toFields.includes('to_company_name')
									? 'to_company_name'
									: null,
							].filter(Boolean) as string[],
						'to'
					)}
				</>
			)}

			{otherFields.length > 0 && renderFields(otherFields, 'other')}
		</Layout>
	);
};

export default MrcContent;